<script setup lang="ts">
import type { AutoplayDurationType } from '@@/bits/autoplay'
import { AUTOPLAY_DURATIONS } from '@@/bits/autoplay'
import { __ } from '@@/bits/intl'
import OzDivider, { OzDividerColorScheme } from '@@/library/v4/components/OzDivider.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzRowToggle from '@@/library/v4/components/OzRowToggle.vue'
import { computed } from 'vue'

const props = defineProps<{
  darkMode: boolean
  selectedAutoplayDuration: AutoplayDurationType
  isAutoplayLooping: boolean
  toggleLoopButtonText?: string
  testIdPrefix?: string
  // Analytics - should be updated to use event tracker later instead of data-pepin-id
  durationTypeAnalyticsIdPrefix?: string
  loopAnalyticsId?: string
}>()

const emit = defineEmits<{
  (e: 'select-duration-type', value: AutoplayDurationType): void
  (e: 'toggle-loop'): void
}>()

const handleSetDurationType = (value: AutoplayDurationType) => {
  emit('select-duration-type', value)
}

const autoplayDurationGroup = computed(() => {
  const autoplayDurations = Object.keys(AUTOPLAY_DURATIONS).map((duration) => ({
    text: AUTOPLAY_DURATIONS[duration as AutoplayDurationType].long,
    value: duration as AutoplayDurationType,
    checked: props.selectedAutoplayDuration === duration,
    role: 'menuitemradio',
  }))

  return autoplayDurations
})

const autoplayDescription = computed(() => {
  if (props.isAutoplayLooping) {
    return props.selectedAutoplayDuration === 'auto'
      ? __(
          'Autoplaying at a duration that is automatically calculated based on the number of words and attachments on the slide. It will loop after finishing all the slides.',
        )
      : __('Autoplaying at a duration of %{duration} seconds. It will loop after finishing all the slides.', {
          duration: props.selectedAutoplayDuration,
        })
  }

  return props.selectedAutoplayDuration === 'auto'
    ? __(
        'Autoplaying at a duration that is automatically calculated based on the number of words and attachments on the slide. It will not loop after finishing all the slides.',
      )
    : __('Autoplaying at a duration of %{duration} seconds. It will not loop after finishing all the slides', {
        duration: props.selectedAutoplayDuration,
      })
})
</script>

<template>
  <div
    :class="[
      'p-0 rounded-2xl overflow-auto grid grid-flow-row grid-cols-1',
      {
        'bg-light-ui-100 text-dark-text-100': !darkMode,
        'bg-dark-ui-100 text-light-text-100': darkMode,
      },
    ]"
    :style="{
      width: '256px',
      zIndex: 1000,
    }"
    role="menu"
    :data-testid="`${testIdPrefix}AutoplayMenu`"
  >
    <p id="autoplay-description" class="sr-only">
      {{ autoplayDescription }}
    </p>
    <div :class="['flex flex-col']">
      <div v-for="(row, rowIndex) in autoplayDurationGroup" :key="rowIndex">
        <div :class="['rounded-lg', 'my-1 px-1']">
          <div
            :class="[
              'flex flex-row justify-between items-center',
              'cursor-pointer',
              'rounded-xl',
              'py-2 px-3',
              'focus-visible:ring-solid focus-visible:ring-2',
              {
                'hover:bg-light-ui-200 focus-visible:bg-light-ui-200 focus-visible:ring-grape-500': !darkMode,
                'hover:bg-dark-ui-200 focus-visible:bg-dark-ui-200 focus-visible:ring-canary-500': darkMode,
              },
            ]"
            :tabindex="0"
            :role="row.role"
            :aria-checked="row.checked"
            :data-pepin-id="`${durationTypeAnalyticsIdPrefix}-${row.value}`"
            data-pepin-trigger="click"
            :data-testid="`autoPlayMenuDuration-${row.value}`"
            @click="handleSetDurationType(row.value)"
            @keydown.enter.stop.prevent="handleSetDurationType(row.value)"
            @keydown.space.stop.prevent="handleSetDurationType(row.value)"
          >
            <span>
              {{ row.text }}
            </span>

            <OzIcon
              :name="row.checked ? 'radio_selected' : 'radio_unselected'"
              :size="24"
              :class="[
                row.checked
                  ? {
                      'text-grape-500': !darkMode,
                      'text-canary-500': darkMode,
                    }
                  : {
                      'text-dark-text-200': !darkMode,
                      'text-light-text-200': darkMode,
                    },
              ]"
            />
          </div>
        </div>
        <OzDivider
          v-if="rowIndex < autoplayDurationGroup.length - 1"
          class="mx-4"
          :color-scheme="OzDividerColorScheme.Solid"
          :dark-mode="darkMode"
        />
      </div>
      <div
        :class="[
          'h-2',
          {
            'bg-grey-100': !darkMode,
            'bg-grey-800': darkMode,
          },
        ]"
      />
      <div :class="['rounded-lg', 'px-4 py-2', 'my-1']">
        <OzRowToggle
          :value="isAutoplayLooping"
          :label="toggleLoopButtonText ?? __('Loop slides')"
          class="w-full p-0 m-0"
          :min-height="0"
          :data-pepin-id="loopAnalyticsId"
          data-pepin-trigger="click"
          :dark-mode="darkMode"
          :data-testid="`${testIdPrefix}AutoplayLoopingToggle`"
          @click="emit('toggle-loop')"
        >
          <p
            :class="[
              'text-body-small',
              {
                'text-dark-text-100': !darkMode,
                'text-light-text-100': darkMode,
              },
            ]"
          >
            {{ toggleLoopButtonText ?? __('Loop slides') }}
          </p>
        </OzRowToggle>
      </div>
    </div>
  </div>
</template>
